import { useEffect, useState } from 'react';
import { Modal } from '@/components/primitives/Modal';
import { Button } from '@/components/primitives/Button';
import { startRenderJob } from '@/services/renders';
import type { ComplianceOverall, RenderOutputResolution } from '@/types/api';

const RESOLUTION_OPTIONS: { value: RenderOutputResolution; label: string }[] = [
  { value: 'HD', label: 'HD (1280×720)' },
  { value: 'FHD', label: 'FHD (1920×1080)' },
  { value: 'UHD', label: 'UHD (3840×2160)' },
];

export interface StartRenderModalProps {
  isOpen: boolean;
  onClose: () => void;
  creativeId: number;
  creativeName: string;
  onStarted?: () => void;
  /** When ``FAIL``, owner/editor may pass ``force`` (API-enforced). */
  complianceOverall?: ComplianceOverall | string | null;
}

export default function StartRenderModal({
  isOpen,
  onClose,
  creativeId,
  creativeName,
  onStarted,
  complianceOverall,
}: StartRenderModalProps) {
  const [resolution, setResolution] = useState<RenderOutputResolution>('HD');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [force, setForce] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setResolution('HD');
    setError('');
    setSubmitting(false);
    setForce(false);
  }, [isOpen]);

  async function handleStart() {
    setError('');
    setSubmitting(true);
    try {
      await startRenderJob(creativeId, resolution, {
        force: complianceOverall === 'FAIL' ? force : false,
      });
      onStarted?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start render');
    } finally {
      setSubmitting(false);
    }
  }

  const footerBase = (
    <div className="flex justify-end gap-3 w-full">
      <Button variant="ghost" onClick={onClose} disabled={submitting}>
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={() => void handleStart()}
        disabled={submitting}
        className="min-w-[120px]"
      >
        {submitting ? 'Starting…' : 'Render job'}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Export Video"
      subtitle={`Start a render job for ${creativeName}`}
      width="max-w-md"
      footer={footerBase}
    >
      <div className="flex flex-col">
        {complianceOverall === 'FAIL' ? (
          <label className="mb-4 flex cursor-pointer items-start gap-2 text-sm text-on-surface">
            <input
              type="checkbox"
              checked={force}
              onChange={(e) => setForce(e.target.checked)}
              className="mt-1"
            />
            <span>
              Force export despite compliance failure (allowed for owner and editor only).
            </span>
          </label>
        ) : null}
        <p className="text-sm text-on-surface-variant mb-6">
          The current saved creative (or latest on the server after you save) is snapshotted when
          the job starts.
        </p>

        <p className="text-xs text-on-surface-variant mb-3 uppercase tracking-wider font-bold">
          Output resolution
        </p>

        <div className="flex flex-col gap-3">
          {RESOLUTION_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-all ${
                resolution === opt.value
                  ? 'border-primary bg-primary-container/50 text-on-surface'
                  : 'border-outline-variant/20 text-on-surface-variant hover:border-primary/30 hover:bg-surface-container-low'
              }`}
            >
              <input
                type="radio"
                name="output-resolution"
                value={opt.value}
                checked={resolution === opt.value}
                onChange={() => setResolution(opt.value)}
                className="accent-primary w-4 h-4 cursor-pointer"
              />
              <span className="text-sm font-bold tracking-wide">{opt.label}</span>
            </label>
          ))}
        </div>

        {error ? (
          <div className="mt-4 p-3 rounded-lg bg-error-container text-on-error-container text-sm font-medium">
            {error}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
